import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/layout";
import Container from "../components/layout/container";
import { Truck, MapPin, CheckCircle2 } from "lucide-react";
import BkashPaymentModal from "../components/bkash";
import { LoadingModal } from "../components/loading";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../utils/api";
import { trackEvent } from "../metapixel";

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-black/10 bg-white shadow-sm ${className}`}
  >
    {children}
  </div>
);

const PrimaryButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const GhostButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-semibold text-black/80 shadow-sm transition hover:bg-black/5 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Field = ({ label, children, hint }) => (
  <div className="space-y-2 min-w-0">
    <div className="text-sm font-extrabold text-black/75">{label}</div>
    {children}
    {hint ? (
      <div className="text-xs font-medium text-black/50">{hint}</div>
    ) : null}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={`h-11 w-full rounded-xl border border-black/10 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-orange-600/25 ${
      props.className ?? ""
    }`}
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={`min-h-[110px] w-full resize-none rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-orange-600/25 ${
      props.className ?? ""
    }`}
  />
);

const Row = ({ label, value, valueClass = "text-black/80" }) => (
  <div className="flex items-center justify-between gap-3 min-w-0">
    <div className="text-sm font-semibold text-black/60">{label}</div>
    <div className={`text-sm font-extrabold ${valueClass}`}>{value}</div>
  </div>
);

const moneyBDT = (n) => `BDT ${Number(n || 0).toLocaleString("en-US")}`;

export default function CheckoutPage() {
  // ----- Demo cart subtotal (replace with your cart subtotal) -----
  const [cart, setCart] = useState([]);

  const handleCartUpdate = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCart(cart);
  };

  useEffect(() => {
    handleCartUpdate();
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
    };
  }, []);

  const cartSubtotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cart]);

  // ----- Delivery rules -----
  const DELIVERY_TYPES = useMemo(
    () => [
      { key: "express", label: "Express Delivery", value: 12, onlyDhaka: true },
      {
        key: "regular",
        label: "Regular Delivery",
        value: 48,
        onlyDhaka: false,
      },
    ],
    []
  );

  const REGULAR_METHODS = useMemo(
    () => [
      {
        key: "inside_dhaka",
        label: "Pathao Courier (Inside Dhaka)",
        charge: 70,
      },
      {
        key: "suburban",
        label:
          "Pathao Courier Suburban (Gazipur, Keranigong, Naryangong, Savar)",
        charge: 80, // ⚠️ change if you have the real charge
      },
      {
        key: "outside_dhaka",
        label: "Pathao Courier (Outside Dhaka)",
        charge: 110,
      },
    ],
    []
  );

  const EXPRESS_METHOD = useMemo(
    () => ({ key: "pathao_express", label: "Pathao Express", charge: 130 }),
    []
  );

  // ---- Express cutoff logic ----
  const EXPRESS_CUTOFF_HOUR = 11;
  const EXPRESS_CUTOFF_MINUTE = 30;

  const now = new Date();
  const cutoff = new Date();
  cutoff.setHours(EXPRESS_CUTOFF_HOUR, EXPRESS_CUTOFF_MINUTE, 0, 0);

  const isBeforeExpressCutoff = now <= cutoff;

  const expressDeliveryMessage = isBeforeExpressCutoff
    ? "Order placed before 11:30 AM — eligible for same-day delivery."
    : "Order placed after 11:30 AM — will be processed next day.";

  // ---- Express availability window ----

  const minutesNow = now.getHours() * 60 + now.getMinutes();

  // 11:30 AM → 690 minutes
  const EXPRESS_DISABLE_START = 11 * 60 + 30;

  // 8:00 PM → 1200 minutes
  const EXPRESS_DISABLE_END = 20 * 60;

  // Express is allowed if OUTSIDE the disabled window
  const isExpressTimeAllowed =
    minutesNow < EXPRESS_DISABLE_START || minutesNow >= EXPRESS_DISABLE_END;

  // ----- Form state -----
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    address: "",
    gift_wrap: false,
    note: "",
  });

  const handleAddToCart = () => {
    let cart = [];

    try {
      const storedCart = JSON.parse(localStorage.getItem("cart"));
      if (Array.isArray(storedCart)) {
        cart = storedCart;
      }
    } catch (err) {
      cart = [];
    }

    const item = {
      pid: "gift-wrap",
      sku: "GIFT-WRAP",
      variant: "Standard Gift Wrap",
      title: "Gift Wrapping",
      img: "https://retrorevive.s3.ap-southeast-1.amazonaws.com/GIFT_WRAP.jpg",
      quantity: 1,
      price: 100,
    };

    const existingIndex = cart.findIndex(
      (cartItem) => cartItem.sku === item.sku
    );

    if (existingIndex >= 0) {
      cart.pop(existingIndex);
    } else {
      cart.push(item);
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleGiftWrapChange = (e) => {
    const { checked } = e.target;
    setForm((p) => ({ ...p, gift_wrap: checked }));
    handleAddToCart();
  };

  const [deliveryType, setDeliveryType] = useState("regular"); // default
  const [regularMethod, setRegularMethod] = useState("inside_dhaka"); // default
  const [expressMethod] = useState("pathao_express"); // fixed

  const cityNormalized = (form.city || "").trim().toLowerCase();
  const isDhaka = cityNormalized === "dhaka";

  // Auto-select best regular method based on city
  useEffect(() => {
    if (!form.city) return;

    if (isDhaka) {
      setRegularMethod("inside_dhaka");
      return;
    }

    const suburbanCities = [
      "gazipur",
      "keranigong",
      "keraniganj",
      "narayangong",
      "narayanganj",
      "savar",
    ];
    if (suburbanCities.includes(cityNormalized)) {
      setRegularMethod("suburban");
    } else {
      setRegularMethod("outside_dhaka");
    }
  }, [form.city, isDhaka, cityNormalized]);

  const deliveryCharge = useMemo(() => {
    if (deliveryType === "express") return EXPRESS_METHOD.charge;

    const m = REGULAR_METHODS.find((x) => x.key === regularMethod);
    return m?.charge ?? 0;
  }, [deliveryType, regularMethod, REGULAR_METHODS, EXPRESS_METHOD]);

  const total = cartSubtotal + deliveryCharge;

  const canUseExpress = isDhaka && isExpressTimeAllowed;

  // Auto-enforce: express only for Dhaka
  useEffect(() => {
    if (deliveryType === "express" && !isDhaka) {
      setDeliveryType("regular");
    }
  }, [deliveryType, isDhaka]);

  useEffect(() => {
    if (deliveryType === "express" && !canUseExpress) {
      setDeliveryType("regular");
    }
  }, [deliveryType, canUseExpress]);

  const isOutsideDhaka =
    !isDhaka &&
    ![
      "gazipur",
      "keranigong",
      "keraniganj",
      "narayangong",
      "narayanganj",
      "savar",
    ].includes(cityNormalized);

  let paymentMode;
  // FULL_PREPAY | PARTIAL_OR_FULL_PREPAY | CHOICE

  if (deliveryType === "express") {
    paymentMode = "FULL_PREPAY";
  } else if (isOutsideDhaka) {
    paymentMode = "PARTIAL_OR_FULL_PREPAY";
  } else {
    paymentMode = "CHOICE";
  }

  const [payOption, setPayOption] = useState("COD");
  const [showBkash, setShowBkash] = useState(false);
  const [trxId, setTrxId] = useState("");
  const [loading, setLoading] = useState(false);

  // COD | PAY_NOW

  useEffect(() => {
    if (paymentMode === "FULL_PREPAY") {
      setPayOption("PAY_NOW");
    }
    if (paymentMode === "PARTIAL_OR_FULL_PREPAY") {
      setPayOption("COD"); // default → pay only delivery charge
    }
  }, [paymentMode]);

  const advanceAmount = useMemo(() => {
    if (paymentMode === "FULL_PREPAY") return total;

    if (paymentMode === "PARTIAL_OR_FULL_PREPAY") {
      return payOption === "PAY_NOW" ? total : 110;
    }

    if (paymentMode === "CHOICE" && payOption === "PAY_NOW") return total;

    return 0;
  }, [paymentMode, payOption, total]);

  const codAmount = total - advanceAmount;

  const payableNow = useMemo(() => {
    if (paymentMode === "FULL_PREPAY") return total;

    if (paymentMode === "PARTIAL_OR_FULL_PREPAY") {
      return payOption === "PAY_NOW" ? total : 110;
    }

    if (paymentMode === "CHOICE" && payOption === "PAY_NOW") {
      return total;
    }

    return 0; // COD only
  }, [paymentMode, payOption, total]);

  const confirmButtonLabel =
    payableNow > 0 ? `Pay ${moneyBDT(payableNow)}` : "Place Order (COD)";

  const placeOrderDisabled =
    !form.name.trim() ||
    !form.phone.trim() ||
    !form.email.trim() ||
    !form.city.trim() ||
    !form.address.trim();

  const navigate = useNavigate();

  trackEvent("InitiateCheckout", {
    value: total,
    currency: "BDT",
    num_items: cart.length,
  });

  const handleOrderConfirm = async (trxId) => {
    if (placeOrderDisabled) return;
    if (form.email.trim().length === 0) {
      alert("Email is required.");
      return;
    } else if (!form.email.includes("@") || !form.email.includes(".")) {
      alert("Please enter a valid email address.");
      return;
    } else if (form.phone.trim().length < 11) {
      alert("Please enter a valid phone number.");
      return;
    } else if (form.address.trim().length < 5) {
      alert("Please enter a valid address.");
      return;
    }
    setLoading(true);
    const data = {
      merchant_order_id: Math.floor(Math.random() * 1000),
      recipient_name: form.name,
      recipient_phone: form.phone,
      recipient_address: form.address,
      email: form.email,
      delivery_type: deliveryType === "express" ? 12 : 48,
      item_type: 2, // general item
      item_quantity: 1,
      item_weight: deliveryType === "express" ? 0.2 : 0.5, // kg
      item_description: cart
        .map((p) => `${p.sku} [${p.variant} - ${p.quantity}Pc(s)]`)
        .join(", "),
      products: cart,
      order_total: total,
      trxid: trxId,
      payment_status: payOption === "PAY_NOW" ? "Paid" : "Unpaid",
      amount_paid: payableNow,
      delivery_charge: deliveryCharge,
      gift_wrap: form.gift_wrap,
      note: form.note,
    };
    axios
      .post(`${API}/order/create/direct`, data)
      .then((res) => {
        setLoading(false);
        localStorage.removeItem("cart");
        window.dispatchEvent(new Event("cartUpdated"));
        navigate(`/order/success?orderId=${res.data._id}`, {
          state: { order: res.data },
        });
      })
      .catch((err) => {
        setLoading(false);
        alert("Failed to place order. Please try again.");
        console.error("Order placement error:", err);
      });
  };

  const toTitleCase = (str = "") =>
    str
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <Layout>
      <main className="py-8 sm:py-10 overflow-x-hidden">
        <Container>
          {loading && <LoadingModal text="Placing your order..." />}
          {/* Header */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-black tracking-tight text-black/85 sm:text-3xl">
                Checkout
              </h1>
              <p className="mt-1 text-sm font-medium text-black/55">
                Enter delivery details and choose your shipping method.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3 min-w-0">
            {/* Left: form */}
            <div className="lg:col-span-2 min-w-0">
              <Card className="p-4 sm:p-6 min-w-0">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-black/60" />
                  <div className="text-sm font-extrabold text-black/80">
                    Delivery Information
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 min-w-0">
                  <Field label="Full Name">
                    <Input
                      value={form.name}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="Your name"
                    />
                  </Field>

                  <Field label="Phone Number">
                    <Input
                      value={form.phone}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, phone: e.target.value }))
                      }
                      placeholder="01XXXXXXXXX"
                      inputMode="tel"
                    />
                  </Field>

                  <Field label="Email">
                    <Input
                      value={form.email}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, email: e.target.value }))
                      }
                      placeholder="you@example.com"
                      type="email"
                    />
                  </Field>

                  <Field
                    label="Delivery City"
                    hint="Type Dhaka to enable Express Delivery."
                  >
                    <Input
                      value={form.city}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, city: e.target.value }))
                      }
                      onBlur={() =>
                        setForm((p) => ({ ...p, city: toTitleCase(p.city) }))
                      }
                      placeholder="Dhaka / Gazipur / Chattogram..."
                    />
                  </Field>

                  <div className="sm:col-span-2">
                    <Field label="Full Address">
                      <Textarea
                        value={form.address}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, address: e.target.value }))
                        }
                        onBlur={() =>
                          setForm((p) => ({
                            ...p,
                            address: toTitleCase(p.address),
                          }))
                        }
                        placeholder="House, Road, Area, Thana, District..."
                      />
                    </Field>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-3 sm:col-span-2">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        name="gift_wrap"
                        checked={form.gift_wrap}
                        // onChange={handleChange}
                        onChange={(e) => {
                          handleGiftWrapChange(e);
                        }}
                        className="mt-1 h-4 w-4 rounded border-slate-300"
                      />
                      <div className="flex">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900">
                              Gift Wrapping
                            </p>
                            <p className="text-xs text-slate-500">
                              Add wrapping to make your gift extra special.
                            </p>
                          </div>
                        </div>

                        {/* Optional note */}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Type */}
                <div className="mt-8">
                  <div className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-black/60" />
                    <div className="text-sm font-extrabold text-black/80">
                      Delivery Type
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col lg:grid grid-cols-1 gap-3 ">
                    {/* Express */}
                    <label
                      className={`cursor-pointer rounded-2xl border p-4 shadow-sm transition min-w-0 ${
                        deliveryType === "express"
                          ? "border-orange-600 bg-orange-600/10"
                          : "border-black/10 bg-white hover:bg-black/5"
                      } ${canUseExpress ? "" : "opacity-60"}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="deliveryType"
                          value="express"
                          checked={deliveryType === "express"}
                          onChange={() =>
                            canUseExpress && setDeliveryType("express")
                          }
                          className="mt-1 accent-orange-600"
                          disabled={!canUseExpress}
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-black/80">
                            Express Delivery
                          </div>
                          <div className="mt-1 text-xs font-medium text-black/55">
                            Delivery in{" "}
                            {
                              DELIVERY_TYPES.find((x) => x.key === "express")
                                ?.value
                            }{" "}
                            hours
                          </div>
                          <div className="mt-2 text-xs font-bold text-black/55">
                            Method:{" "}
                            <span className="text-black/75">
                              Pathao Express
                            </span>
                          </div>
                          <div className="mt-1 text-sm font-black text-orange-700">
                            {moneyBDT(EXPRESS_METHOD.charge)}
                          </div>
                          {deliveryType === "express" && (
                            <div
                              className={`mt-2 text-xs font-semibold ${
                                isBeforeExpressCutoff
                                  ? "text-emerald-700"
                                  : "text-orange-700"
                              }`}
                            >
                              {expressDeliveryMessage}
                            </div>
                          )}

                          {!canUseExpress && (
                            <div className="mt-2 text-xs font-semibold text-red-600">
                              Only available for Dhaka city
                            </div>
                          )}
                          {!isExpressTimeAllowed && (
                            <div className="mt-2 text-xs font-semibold text-red-600">
                              Express delivery is unavailable between 11:30 AM
                              and 8:00 PM.
                            </div>
                          )}
                        </div>
                      </div>
                    </label>

                    {/* Regular */}
                    <label
                      className={`cursor-pointer rounded-2xl border p-4 shadow-sm transition min-w-0 ${
                        deliveryType === "regular"
                          ? "border-orange-600 bg-orange-600/10"
                          : "border-black/10 bg-white hover:bg-black/5"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="deliveryType"
                          value="regular"
                          checked={deliveryType === "regular"}
                          onChange={() => setDeliveryType("regular")}
                          className="mt-1 accent-orange-600"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold text-black/80">
                            Regular Delivery
                          </div>
                          <div className="mt-1 text-xs font-medium text-black/55">
                            Delivery in{" "}
                            {
                              DELIVERY_TYPES.find((x) => x.key === "regular")
                                ?.value
                            }{" "}
                            hours
                          </div>

                          <div className="mt-3">
                            <div className="text-xs font-bold text-black/60">
                              Select Method
                            </div>

                            <select
                              className="mt-2 h-11 w-full rounded-xl border border-black/10 bg-white px-3 text-sm font-bold text-black/70 outline-none"
                              value={regularMethod}
                              onChange={(e) => setRegularMethod(e.target.value)}
                            >
                              {REGULAR_METHODS.map((m) => (
                                <option key={m.key} value={m.key}>
                                  {m.label} — {moneyBDT(m.charge)}
                                </option>
                              ))}
                            </select>

                            <div className="mt-2 text-xs font-medium text-black/55">
                              Auto-selected based on your city, you can change
                              it anytime.
                            </div>
                          </div>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Actions */}
                {/* <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
                  <GhostButton className="h-11 w-full sm:w-auto">
                    Back to Cart
                  </GhostButton>
                  <PrimaryButton
                    className="h-11 w-full sm:w-auto"
                    disabled={placeOrderDisabled}
                  >
                    Place Order
                  </PrimaryButton>
                </div> */}

                {placeOrderDisabled && (
                  <div className="mt-3 text-xs font-semibold text-black/50">
                    Please fill in all fields to place your order.
                  </div>
                )}
              </Card>
            </div>

            {/* Right: summary */}
            <div className="lg:col-span-1 min-w-0">
              <Card className="p-4 sm:p-6 lg:sticky  min-w-0">
                <div className="text-sm font-extrabold text-black/80">
                  Order Summary
                </div>
                <div className="mt-1 text-xs font-medium text-black/50">
                  Delivery charge depends on city & selected method.
                </div>

                <div className="mt-5 space-y-3">
                  {/* Product Summary */}
                  <div className="rounded-2xl border border-black/10 bg-white p-3">
                    <div className="text-xs font-extrabold text-black/75">
                      Products
                    </div>

                    <div className="mt-3 space-y-3">
                      {cart.map((item) => (
                        <ProductSummaryRow key={item.id} item={item} />
                      ))}
                    </div>
                  </div>

                  <Row label="Subtotal" value={moneyBDT(cartSubtotal)} />
                  <Row
                    label="Delivery"
                    value={moneyBDT(deliveryCharge)}
                    valueClass="text-black/70"
                  />

                  <div className="my-4 h-px bg-black/10" />

                  <Row
                    label={
                      <span className="text-sm font-black text-black/85">
                        Total
                      </span>
                    }
                    value={
                      <span className="text-sm font-black">
                        {moneyBDT(total)}
                      </span>
                    }
                  />

                  {cityNormalized && (
                    <div className="mt-4 rounded-2xl border border-black/10 bg-black/[0.02] p-4">
                      <div className="flex items-center gap-2 text-xs font-extrabold text-black/75">
                        <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                        Delivery Selected
                      </div>

                      <div className="mt-2 text-xs font-medium text-black/55">
                        <div>
                          Type:{" "}
                          <span className="font-bold text-black/70">
                            {deliveryType === "express"
                              ? "Express (12h)"
                              : "Regular (48h)"}
                          </span>
                        </div>
                        <div className="mt-1">
                          Method:{" "}
                          <span className="font-bold text-black/70">
                            {deliveryType === "express"
                              ? "Pathao Express"
                              : REGULAR_METHODS.find(
                                  (m) => m.key === regularMethod
                                )?.label}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 text-xs font-medium text-black/55">
                        {paymentMode === "FULL_PREPAY" && (
                          <div>
                            Pay now: <strong>{moneyBDT(total)}</strong>
                          </div>
                        )}

                        {paymentMode === "PARTIAL_OR_FULL_PREPAY" &&
                          payOption === "COD" && (
                            <>
                              <div>
                                Pay now (Delivery):{" "}
                                <strong>{moneyBDT(110)}</strong>
                              </div>
                              <div>
                                Pay on delivery:{" "}
                                <strong>{moneyBDT(codAmount)}</strong>
                              </div>
                            </>
                          )}

                        {paymentMode === "PARTIAL_OR_FULL_PREPAY" &&
                          payOption === "PAY_NOW" && (
                            <div>
                              Pay now: <strong>{moneyBDT(total)}</strong>
                            </div>
                          )}

                        {paymentMode === "CHOICE" && payOption === "COD" && (
                          <div>
                            Pay on delivery: <strong>{moneyBDT(total)}</strong>
                          </div>
                        )}

                        {paymentMode === "CHOICE" &&
                          payOption === "PAY_NOW" && (
                            <div>
                              Pay now: <strong>{moneyBDT(total)}</strong>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {cityNormalized && (
                    <div className="mt-6 rounded-2xl border border-black/10 bg-white p-4">
                      <div className="text-sm font-extrabold text-black/80">
                        Payment Method
                      </div>

                      {/* EXPRESS */}
                      {paymentMode === "FULL_PREPAY" && (
                        <div className="mt-2 text-xs font-semibold text-red-600">
                          Express delivery requires full payment in advance.
                        </div>
                      )}

                      {/* OUTSIDE DHAKA */}
                      {paymentMode === "PARTIAL_OR_FULL_PREPAY" && (
                        <div className="mt-3 space-y-2">
                          <div className="text-xs font-semibold text-orange-700">
                            Delivery charge BDT 110 must be paid in advance.
                          </div>

                          <label className="flex items-center gap-2 text-sm font-bold text-black/70">
                            <input
                              type="radio"
                              name="payOption"
                              value="COD"
                              checked={payOption === "COD"}
                              onChange={() => setPayOption("COD")}
                              className="accent-orange-600"
                            />
                            Pay delivery charge only (BDT 110) + rest COD
                          </label>

                          <label className="flex items-center gap-2 text-sm font-bold text-black/70">
                            <input
                              type="radio"
                              name="payOption"
                              value="PAY_NOW"
                              checked={payOption === "PAY_NOW"}
                              onChange={() => setPayOption("PAY_NOW")}
                              className="accent-orange-600"
                            />
                            Pay full amount now
                          </label>
                        </div>
                      )}

                      {/* REGULAR DHAKA */}
                      {paymentMode === "CHOICE" && (
                        <div className="mt-3 space-y-2">
                          <label className="flex items-center gap-2 text-sm font-bold text-black/70">
                            <input
                              type="radio"
                              name="payOption"
                              value="COD"
                              checked={payOption === "COD"}
                              onChange={() => setPayOption("COD")}
                              className="accent-orange-600"
                            />
                            Cash on Delivery
                          </label>

                          <label className="flex items-center gap-2 text-sm font-bold text-black/70">
                            <input
                              type="radio"
                              name="payOption"
                              value="PAY_NOW"
                              checked={payOption === "PAY_NOW"}
                              onChange={() => setPayOption("PAY_NOW")}
                              className="accent-orange-600"
                            />
                            Pay Now (bKash)
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  {cityNormalized && (
                    <PrimaryButton
                      className="mt-3 w-full h-11"
                      disabled={placeOrderDisabled}
                      onClick={() => {
                        if (payableNow > 0) {
                          setShowBkash(true);
                        } else {
                          handleOrderConfirm(trxId);
                        }
                      }}
                    >
                      {confirmButtonLabel}
                    </PrimaryButton>
                  )}
                </div>
              </Card>
            </div>
          </div>
          <BkashPaymentModal
            open={showBkash}
            amount={moneyBDT(payableNow)}
            onClose={() => setShowBkash(false)}
            trxId={trxId}
            setTrxId={setTrxId}
            onConfirm={(trxId) => {
              setShowBkash(false);

              // FINAL ORDER PAYLOAD
              const orderPayload = {
                paymentMethod: "BKASH",
                trxId,
                advancePaid: payableNow,
                paymentType:
                  payableNow === total ? "FULL_PREPAY" : "PARTIAL_PREPAY",
              };

              handleOrderConfirm(trxId);

              // 👉 call API here
            }}
          />
        </Container>
      </main>
    </Layout>
  );
}

const ProductSummaryRow = ({ item }) => {
  const lineTotal = item.price * item.quantity;

  return (
    <div className="flex gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="line-clamp-2 text-xs font-bold text-black/75">
          {item.title}
        </div>

        <div className="mt-1 text-[11px] font-medium text-black/50">
          Variant: {item.variant} · Qty: {item.quantity}
        </div>
      </div>

      <div className="shrink-0 text-xs font-extrabold text-black/70">
        {moneyBDT(lineTotal)}
      </div>
    </div>
  );
};
